/**
 * Generic Master CRUD page (Admin → Control Centre → any master).
 *
 * Provides: search, add, edit inline, duplicate, activate/deactivate, delete,
 * dependency-aware delete, export CSV.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, RefreshCw, Copy, Trash2, RotateCw, Download, Upload, Save, X,
} from "lucide-react";

export default function MasterPage() {
  const { name } = useParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // {isNew, data}
  const [depsDialog, setDepsDialog] = useState(null);

  const load = () => api.get(`/admin/masters/${name}`).then(({ data }) => {
    setMeta({ label: data.label, fields: data.fields });
    setRows(data.items || []);
  });
  useEffect(() => { setRows([]); setEditing(null); load(); }, [name]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!meta) return <div className="text-sm text-slate-500 p-4">Loading…</div>;

  const idField = meta.fields[0];

  const save = async (record, isNew) => {
    try {
      if (isNew) {
        await api.post(`/admin/masters/${name}`, record);
        toast.success("Created");
      } else {
        await api.patch(`/admin/masters/${name}/${record[idField]}`, record);
        toast.success("Updated");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/masters/${name}/${id}/duplicate`);
      toast.success("Duplicated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const archive = async (id) => {
    try {
      await api.delete(`/admin/masters/${name}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const hardDelete = async (id) => {
    try {
      await api.delete(`/admin/masters/${name}/${id}`, { params: { hard: true } });
      toast.success("Deleted");
      setDepsDialog(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.dependencies) {
        setDepsDialog({ id, ...detail });
      } else {
        toast.error(detail || "Failed");
      }
    }
  };

  const restore = async (id) => {
    await api.post(`/admin/masters/${name}/${id}/restore`);
    toast.success("Restored");
    load();
  };

  const doExport = () => window.open(
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/masters/${name}/export`,
    "_blank"
  );

  return (
    <div className="max-w-6xl space-y-4" data-testid={`master-${name}`}>
      <Link to="/admin/control-centre" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Control Centre
      </Link>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Master</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1">{meta.label}</h1>
          <div className="text-xs text-slate-500 mt-1">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
        </div>
        <button onClick={load} className="h-9 px-3 text-xs rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        <button onClick={doExport} className="h-9 px-3 text-xs rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1" data-testid="master-export"><Download className="h-3.5 w-3.5" /> Export</button>
        <button
          onClick={() => setEditing({ isNew: true, data: Object.fromEntries(meta.fields.map((f) => [f, ""])) })}
          className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1"
          data-testid="master-add"
        ><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>

      {editing && (
        <RowForm
          fields={meta.fields}
          value={editing.data}
          isNew={editing.isNew}
          onCancel={() => setEditing(null)}
          onSave={(rec) => save(rec, editing.isNew)}
        />
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.08em] font-semibold text-slate-500">
            <tr>
              {meta.fields.map((f) => (
                <th key={f} className="px-3 py-2 text-left font-semibold">{f.replace(/_/g, " ")}</th>
              ))}
              <th className="px-3 py-2 text-left font-semibold">Active</th>
              <th className="px-3 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={meta.fields.length + 2} className="px-4 py-8 text-slate-400 text-center">No records</td></tr>
            ) : rows.map((r) => (
              <tr key={r[idField]} className="border-t border-slate-100 hover:bg-slate-50/70">
                {meta.fields.map((f) => (
                  <td key={f} className="px-3 py-2 text-slate-800">{formatCell(r[f])}</td>
                ))}
                <td className="px-3 py-2">
                  <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (r.active !== false ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-50 border-slate-200")}>
                    {r.active === false ? "inactive" : "active"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <IconAction icon={Copy} label="Duplicate" onClick={() => duplicate(r[idField])} testid={`row-dup-${r[idField]}`} />
                  <IconAction icon={RotateCw} label="Edit" onClick={() => setEditing({ isNew: false, data: r })} testid={`row-edit-${r[idField]}`} />
                  {r.active === false ? (
                    <IconAction icon={RotateCw} label="Restore" onClick={() => restore(r[idField])} testid={`row-restore-${r[idField]}`} />
                  ) : (
                    <IconAction icon={Trash2} label="Delete" onClick={() => archive(r[idField])} testid={`row-archive-${r[idField]}`} />
                  )}
                  <IconAction icon={Trash2} label="Delete" onClick={() => hardDelete(r[idField])} testid={`row-del-${r[idField]}`} danger />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {depsDialog && (
        <DependencyDialog
          data={depsDialog}
          onClose={() => setDepsDialog(null)}
          onArchive={() => archive(depsDialog.id).then(() => setDepsDialog(null))}
        />
      )}
    </div>
  );
}

function formatCell(v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-300">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function IconAction({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      title={label}
      aria-label={label}
      onClick={onClick}
      data-testid={testid}
      className={"inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 ml-0.5 " + (danger ? "hover:text-rose-700 hover:bg-rose-50" : "")}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.6} />
    </button>
  );
}

function RowForm({ fields, value, isNew, onCancel, onSave }) {
  const [rec, setRec] = useState(value);
  useEffect(() => setRec(value), [value]);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="master-form">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{isNew ? "Add record" : `Edit ${rec[fields[0]]}`}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {fields.map((f) => (
          <label key={f} className="block">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">{f.replace(/_/g, " ")}</div>
            <input className="input" value={rec[f] ?? ""} disabled={!isNew && f === fields[0]}
              onChange={(e) => setRec({ ...rec, [f]: e.target.value })} data-testid={`master-field-${f}`} />
          </label>
        ))}
        <label className="block">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">active</div>
          <select className="input" value={rec.active === false ? "false" : "true"} onChange={(e) => setRec({ ...rec, active: e.target.value === "true" })}>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </label>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="h-9 px-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md inline-flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel</button>
        <button onClick={() => onSave(rec)} className="h-9 px-3 text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-md inline-flex items-center gap-1" data-testid="master-save"><Save className="h-3.5 w-3.5" /> Save</button>
      </div>
    </div>
  );
}

function DependencyDialog({ data, onClose, onArchive }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="dependency-dialog">
      <div className="bg-white rounded-md border border-slate-200 shadow-xl max-w-md w-full p-6">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-rose-700">Cannot delete</div>
        <h3 className="text-lg font-heading font-semibold text-slate-900 mt-1">This record has transactional history.</h3>
        <p className="text-sm text-slate-600 mt-2">
          Permanent deletion would break audit history and referencing transactions.
          Deactivate instead.
        </p>
        <ul className="mt-3 border border-slate-100 rounded-md divide-y text-sm">
          {(data.dependencies || []).map((d, i) => <li key={i} className="px-3 py-2 text-slate-700">Used by {d}</li>)}
        </ul>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={onArchive} className="h-9 px-3 text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-md inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete instead</button>
        </div>
      </div>
    </div>
  );
}
